import React, { useCallback, useMemo, useRef } from 'react';
import { BulkAddClientRecord, csvHeaders, propFromHeader } from '../types';
import { getRowErrors } from './utils';
import Stack from '../../../../../v2BaseComponents/Stack';
import { View } from 'react-native';
import { colors } from '../../../../../../utils/theme';
import { columnWidths } from './constants';
import GridInput from './GridInput';
import { SetInputRefArgs } from '../../../../../v2BaseComponents/RichTable/types';
import HoverDetectionContainer from '../../../../../v2BaseComponents/HoverDetectionContainer';

export type OnHoverChangeArgs = { isHovered: boolean; rowIndex: number };
export type OnHoverChange = (args: OnHoverChangeArgs) => void;

export type EditRowOwnProps = {
    value: BulkAddClientRecord;
    nextValue?: BulkAddClientRecord;
    onChange: (value: BulkAddClientRecord, index: number) => void;
    index: number;
    onSetInputRef: (args: SetInputRefArgs) => void;
    onHoverChange: OnHoverChange;
    container: React.MutableRefObject<View | null>;
    isDisabled: boolean;
};

export type EditRowProps = EditRowOwnProps;

export const ROW_HEIGHT = 36;

export const EditRow: React.FC<EditRowProps> = (props) => {
    const errors = useMemo(() => getRowErrors(props.value), [props.value]);
    const nextErrors = useMemo(() => (props.nextValue ? getRowErrors(props.nextValue) : []), [props.nextValue]);
    const getHasError = (index: number) => !!errors[index];
    const getPreviousHasError = (index: number) => (index === 0 ? false : getHasError(index - 1));
    const getNextHasError = (index: number) => !!nextErrors[index];
    const hasPendingHoverClear = useRef(false);
    const onHoverChangeHandler = useCallback(
        (isHovered: boolean) => {
            const callOnChange = () => props.onHoverChange({ isHovered, rowIndex: props.index });

            if (isHovered) {
                hasPendingHoverClear.current = false;
                callOnChange();
            } else {
                hasPendingHoverClear.current = true;
                setTimeout(() => {
                    if (hasPendingHoverClear.current) callOnChange();

                    hasPendingHoverClear.current = false;
                }, 400); // delay to allow the possible hover to row actions
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.index, props.onHoverChange],
    );

    return (
        <HoverDetectionContainer onHoverChange={onHoverChangeHandler}>
            <Stack horizontal testID={`grid-row-${props.index}`}>
                {csvHeaders.map((header, index) => (
                    <View
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: ROW_HEIGHT,
                            borderWidth: 1,
                            borderTopWidth: 0,
                            borderColor: !!errors[index] ? colors.Alert : colors.Dividers,
                            borderRightWidth: index === csvHeaders.length - 1 ? undefined : 0,
                            borderLeftColor:
                                getPreviousHasError(index) || errors[index] ? colors.Alert : colors.Dividers,
                            borderBottomColor: getNextHasError(index) ? colors.Alert : undefined,
                            width: columnWidths[propFromHeader(header)],
                        }}
                        key={index}
                    >
                        <GridInput
                            error={errors[index] || null}
                            value={props.value[propFromHeader(header)]}
                            onChange={(value) =>
                                props.onChange({ ...props.value, [propFromHeader(header)]: value }, props.index)
                            }
                            onSetInputRef={props.onSetInputRef}
                            rowIndex={props.index}
                            columnIndex={index}
                            container={props.container}
                            isDisabled={props.isDisabled}
                        />
                    </View>
                ))}
            </Stack>
        </HoverDetectionContainer>
    );
};

export default React.memo(EditRow);
